import React, { useState, useEffect, useRef } from 'react';
import { makeAuthenticatedRequest } from '../core/api/client';

const FY_OPTIONS = ['2025-26','2024-25','2023-24','2022-23'];
const MONTHS_2526 = ['2025-04','2025-05','2025-06','2025-07','2025-08','2025-09','2025-10','2025-11','2025-12','2026-01','2026-02','2026-03'];
const MONTHS_2426 = ['2024-04','2024-05','2024-06','2024-07','2024-08','2024-09','2024-10','2024-11','2024-12','2025-01','2025-02','2025-03'];

const STAGE_LABELS = { 'ENQUIRY':'Enquiry','OFFER':'Offer Sent','ORDER_WON':'Order Won','ORDER_LOST':'Order Lost','PENDING_PRICE':'Pending Price','FEASIBILITY':'Feasibility' };
const STAGE_COLORS = { 'ENQUIRY':{bg:'#EFF4FF',color:'#1570EF'},'OFFER':{bg:'#FFF6ED',color:'#C4320A'},'ORDER_WON':{bg:'#ECFDF3',color:'#039855'},'ORDER_LOST':{bg:'#FEF3F2',color:'#D92D20'},'PENDING_PRICE':{bg:'#F9F5FF',color:'#6941C6'},'FEASIBILITY':{bg:'#F0F9FF',color:'#026AA2'} };
const SP_COLORS = ['#1570EF','#039855','#C4320A','#6941C6','#026AA2','#B54708','#C11574','#344054'];

// Tracker service (Android field-app meeting recordings) — separate FastAPI service
const TRACKER_URL = "https://tracker.alokindia.co.in";

const fmt = (n) => n >= 10000000 ? `₹${(n/10000000).toFixed(1)}Cr` : n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : `₹${n.toLocaleString()}`;
const fmtMonth = (m) => { if(!m) return ''; const [y,mo] = m.split('-'); const names=['','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar']; return `${names[parseInt(mo)]} ${y.slice(2)}`; };
const fyToMonths = (fy) => fy === '2025-26' ? MONTHS_2526 : MONTHS_2426;
const fyToCurrentMonth = (fy) => fy === '2025-26' ? '2026-05' : '2025-05';
const fmtDur = (s) => { if (s == null) return ''; const m = Math.floor(s/60); return `${m}:${String(s%60).padStart(2,'0')}`; };

// ── Is this recording playable? ──
// The tracker currently writes 'uploaded' | 'processing' | 'transcribed' | 'failed',
// but do NOT hard-gate playback on one exact string: the audio file exists from the
// moment of upload, and a renamed/added status ('done', 'ready', …) must not silently
// hide the play button. So: known-ready status, OR any row that clearly has audio
// behind it (a duration or a transcript).
const REC_READY = new Set(['transcribed', 'done', 'ready', 'complete', 'completed', 'processed', 'success']);
const REC_PENDING = new Set(['uploaded', 'processing', 'queued', 'pending', '']);
const recStatus = (r) => String(r?.status ?? '').toLowerCase().trim();
const canPlayRecording = (r) => {
  if (!r || r.id == null) return false;
  if (REC_READY.has(recStatus(r))) return true;
  return r.duration_sec != null || !!r.transcript;   // has audio, whatever the label says
};

export default function MecaPage() {
  const [fy, setFy] = useState('2025-26');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // SP Activity state
  const [spMonth, setSpMonth] = useState('2026-05');
  const [spData, setSpData] = useState(null);
  const [spTotals, setSpTotals] = useState(null);
  const [spLoading, setSpLoading] = useState(false);
  const [spActivities, setSpActivities] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [showLogForm, setShowLogForm] = useState(false);
  const [formData, setFormData] = useState({ salesperson_id:'', salesperson_name:'', region:'', logged_by:'', client_name:'', contact_person:'', activity_date: new Date().toISOString().slice(0,10), activity_type:'', product_discussed:'', notes:'', outcome:'', is_converted:false, sale_value:'', action_type:'', action_note:'', action_due_date:'', is_urgent:false });
  const [formMsg, setFormMsg] = useState('');

  // Meeting recordings (read from the tracker service, not the CRM backend)
  const [recordings, setRecordings] = useState([]);
  const [recError, setRecError] = useState(false);
  const [expandedRec, setExpandedRec] = useState(null);
  // Independent date range for the recordings card (the page FY selector only
  // reaches FY 2025-26, so recordings dated later would otherwise be unreachable)
  const [recFrom, setRecFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0,10);
  });
  const [recTo, setRecTo] = useState(new Date().toISOString().slice(0,10));

  // ── Audio playback ──
  // One shared Audio object, created lazily on first click. It is NOT a JSX
  // <audio ref>: a ref can be null (unmounted / not yet attached), and the old
  // `if (!a) return` then swallowed the click silently — no audio, no request,
  // no error. Building it on demand means it can never be null.
  const audioRef = useRef(null);
  const playingIdRef = useRef(null);                  // avoids stale closures in handlers
  const [playingId, setPlayingId] = useState(null);   // row currently loaded
  const [playPaused, setPlayPaused] = useState(false);
  const [playPos, setPlayPos] = useState(0);          // seconds elapsed
  const [playDur, setPlayDur] = useState(0);          // seconds total
  const [playErrId, setPlayErrId] = useState(null);   // row that failed to load

  const getAudio = () => {
    if (audioRef.current) return audioRef.current;
    const a = new Audio();
    a.preload = 'metadata';
    a.onplay = () => setPlayPaused(false);
    a.onpause = () => setPlayPaused(true);
    a.ontimeupdate = () => setPlayPos(a.currentTime || 0);
    a.onloadedmetadata = () => { if (isFinite(a.duration)) setPlayDur(a.duration); };
    a.onended = () => {
      playingIdRef.current = null;
      setPlayingId(null); setPlayPos(0); setPlayPaused(false);
    };
    a.onerror = () => {
      console.warn('[MecaPage] recording failed to load:', a.src, a.error);
      setPlayErrId(playingIdRef.current);
      playingIdRef.current = null;
      setPlayingId(null); setPlayPaused(false);
    };
    audioRef.current = a;
    return a;
  };

  const togglePlay = (rec) => {
    const a = getAudio();

    // same row -> pause / resume in place
    if (playingIdRef.current === rec.id) {
      if (a.paused) a.play().catch(err => { console.warn('[MecaPage] play() rejected:', err); setPlayErrId(rec.id); });
      else a.pause();
      return;
    }

    // different row -> repoint the single player; that stops whatever was playing
    a.pause();
    setPlayErrId(null);
    setPlayPos(0);
    setPlayDur(rec.duration_sec || 0);   // show the known length until metadata lands

    const url = `${TRACKER_URL}/api/track/recording/file/${rec.id}`;
    a.src = url;
    a.load();                            // kick the fetch even if play() is deferred
    playingIdRef.current = rec.id;
    setPlayingId(rec.id);
    a.play().catch(err => {
      console.warn('[MecaPage] play() rejected for', url, err);
      setPlayErrId(rec.id);
      playingIdRef.current = null;
      setPlayingId(null);
    });
  };

  // stop audio when leaving the tab / unmounting
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const SALESPEOPLE = [
    {id:'yadnesh', name:'Yadnesh Shete', region:'DACH'},
    {id:'vinita', name:'Vinita Dharia', region:'Europe'},
    {id:'salvador', name:'Salvador Ramirez', region:'Americas'},
    {id:'mrunali', name:'Mrunali', region:'Domestic'},
    {id:'renu', name:'Renu', region:'Domestic'},
    {id:'jyoti', name:'Jyoti', region:'Domestic'},
    {id:'anjali', name:'Anjali', region:'Domestic'},
    {id:'sanidhi', name:'Sanidhi', region:'Domestic'},
    {id:'nilankshi', name:'Nilankshi', region:'Domestic'},
  ];

  useEffect(() => {
    setLoading(true);
    makeAuthenticatedRequest('/api/v1/meca/?fy=' + fy)
      .then(r => r.json()).then(d => setData(d)).catch(console.error).finally(() => setLoading(false));
  }, [fy]);

  useEffect(() => {
    if (activeTab !== 'spactivity') return;
    loadSpData();
    loadRecordings();
    // Poll the tracker every 20s so status transitions (uploaded → processing →
    // transcribed) appear without a manual refresh. Cleared on unmount/tab change.
    const t = setInterval(loadRecordings, 20000);
    return () => clearInterval(t);
  }, [activeTab, spMonth]);

  const loadRecordings = () => {
    fetch(`${TRACKER_URL}/api/track/recordings`)
      .then(r => { if (!r.ok) throw new Error('tracker'); return r.json(); })
      .then(rows => { setRecordings(Array.isArray(rows) ? rows : []); setRecError(false); })
      .catch(() => setRecError(true));
  };

  const loadSpData = () => {
    setSpLoading(true);
    Promise.all([
      makeAuthenticatedRequest(`/api/v1/meca/summary?month_tag=${spMonth}`).then(r => r.json()),
      makeAuthenticatedRequest(`/api/v1/meca/summary/totals?month_tag=${spMonth}`).then(r => r.json()),
      makeAuthenticatedRequest(`/api/v1/meca/activities?month_tag=${spMonth}&limit=50`).then(r => r.json()),
      makeAuthenticatedRequest(`/api/v1/meca/pending-actions`).then(r => r.json()),
    ]).then(([summary, totals, activities, pending]) => {
      setSpData(summary.salespeople || []);
      setSpTotals(totals);
      setSpActivities(activities.activities || []);
      setPendingActions(pending.pending || []);
    }).catch(console.error).finally(() => setSpLoading(false));
  };

  const handleSpSelect = (sp) => {
    setFormData(f => ({ ...f, salesperson_id: sp.id, salesperson_name: sp.name, region: sp.region }));
  };

  const submitActivity = () => {
    if (!formData.salesperson_id || !formData.client_name || !formData.activity_type || !formData.outcome) {
      setFormMsg('Please fill salesperson, client, type and outcome.');
      return;
    }
    const payload = { ...formData, sale_value: formData.sale_value ? parseFloat(formData.sale_value) : null, logged_by: formData.logged_by || 'Coordinator' };
    makeAuthenticatedRequest('/api/v1/meca/activity', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) })
      .then(r => r.json()).then(d => {
        setFormMsg('✅ Activity saved! ID: ' + d.id);
        setShowLogForm(false);
        setFormData(f => ({ ...f, client_name:'', contact_person:'', product_discussed:'', notes:'', outcome:'', is_converted:false, sale_value:'', action_type:'', action_note:'', action_due_date:'', is_urgent:false }));
        loadSpData();
      }).catch(() => setFormMsg('Error saving. Try again.'));
  };

  const s = data?.summary || {};
  const monthly = data?.monthly || [];
  const pipeline = data?.pipeline || [];
  const scPerf = data?.sc_performance || [];
  const topGrades = data?.top_grades || [];
  const maxEnq = Math.max(...monthly.map(m => m.total_enquiries), 1);
  const maxGrade = Math.max(...topGrades.map(g => g.count), 1);
  const totalPipeline = pipeline.reduce((s,p) => s + p.count, 0);
  const spRecordings = recordings.filter(r => {
    const d = r.visit_date || '';
    if (!d) return false;
    if (recFrom && d < recFrom) return false;
    if (recTo && d > recTo) return false;
    return true;
  });

  const card = (label, value, icon, color, bg) => (
    <div style={{background:'white', border:'1px solid #EAECF0', borderRadius:'14px', padding:'20px', boxShadow:'0 1px 3px rgba(16,24,40,0.05)'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px'}}>
        <p style={{margin:0, fontSize:'12px', color:'#667085', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.5px'}}>{label}</p>
        <span style={{width:'32px', height:'32px', background:bg, borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px'}}>{icon}</span>
      </div>
      <p style={{margin:0, fontSize:'28px', fontWeight:'700', color, fontFamily:'DM Mono, monospace'}}>{value}</p>
    </div>
  );

  return (
    <div style={{minHeight:'100vh', background:'#F7F8FA', fontFamily:"'DM Sans', system-ui, sans-serif"}}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@500&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{background:'white', borderBottom:'1px solid #EAECF0', padding:'18px 28px', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
        <div>
          <h1 style={{margin:0, fontSize:'20px', fontWeight:'700', color:'#101828'}}>MECA Report</h1>
          <p style={{margin:0, fontSize:'13px', color:'#667085'}}>Monthly Effective Customer Acquisition — {fy}</p>
        </div>
        <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
          {activeTab === 'spactivity' && (
            <select value={spMonth} onChange={e => setSpMonth(e.target.value)}
              style={{padding:'8px 14px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'14px', fontWeight:'500', background:'white', cursor:'pointer', outline:'none'}}>
              {fyToMonths(fy).map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
            </select>
          )}
          <select value={fy} onChange={e => { setFy(e.target.value); setSpMonth(fyToCurrentMonth(e.target.value)); }}
            style={{padding:'8px 14px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'14px', fontWeight:'500', background:'white', cursor:'pointer', outline:'none'}}>
            {FY_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>

      <div style={{padding:'24px 28px', maxWidth:'1200px'}}>
        {loading && activeTab !== 'spactivity' ? (
          <div style={{textAlign:'center', padding:'80px', color:'#667085'}}>Loading MECA data...</div>
        ) : (
          <>
            {/* KPI cards — show CRM-based for non-SP tabs, activity-based for SP tab */}
            {activeTab !== 'spactivity' && (
              <div style={{display:'grid', gridTemplateColumns:'repeat(4, minmax(0,1fr))', gap:'16px', marginBottom:'24px'}}>
                {card('Unique Clients', s.total_clients||0, '🏢', '#1570EF', '#EFF4FF')}
                {card('Total Enquiries', s.total_enquiries||0, '📋', '#C4320A', '#FFF6ED')}
                {card('Orders Won', s.orders_won||0, '🏆', '#039855', '#ECFDF3')}
                {card('Revenue Won', fmt(s.revenue||0), '💰', '#6941C6', '#F9F5FF')}
              </div>
            )}
            {activeTab === 'spactivity' && spTotals && (
              <div style={{display:'grid', gridTemplateColumns:'repeat(5, minmax(0,1fr))', gap:'16px', marginBottom:'24px'}}>
                {card('Meetings', spTotals.total_meetings||0, '🤝', '#1570EF', '#EFF4FF')}
                {card('Engagements', spTotals.total_engagements||0, '📞', '#C4320A', '#FFF6ED')}
                {card('Recordings', spRecordings.length, '🎙️', '#026AA2', '#F0F9FF')}
                {card('Conversions', spTotals.total_conversions||0, '🏆', '#039855', '#ECFDF3')}
                {card('Revenue', fmt(spTotals.total_revenue||0), '💰', '#6941C6', '#F9F5FF')}
              </div>
            )}

            {/* Tabs */}
            <div style={{display:'flex', gap:'4px', marginBottom:'20px', background:'white', borderRadius:'10px', padding:'4px', border:'1px solid #EAECF0', width:'fit-content'}}>
              {[
                {id:'overview', label:'📊 Monthly Trend'},
                {id:'pipeline', label:'🔄 Pipeline'},
                {id:'sc', label:'👥 SC Performance'},
                {id:'grades', label:'🔬 Top Grades'},
                {id:'spactivity', label:'🏃 SP Activity'},
              ].map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                  padding:'8px 16px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'500',
                  background: activeTab===t.id ? '#1570EF' : 'transparent',
                  color: activeTab===t.id ? 'white' : '#667085', transition:'all 0.15s',
                }}>{t.label}</button>
              ))}
            </div>

            {/* ── Monthly Trend ── */}
            {activeTab === 'overview' && (
              <div style={{background:'white', border:'1px solid #EAECF0', borderRadius:'14px', overflow:'hidden', boxShadow:'0 1px 3px rgba(16,24,40,0.05)'}}>
                <div style={{padding:'16px 20px', borderBottom:'1px solid #F2F4F7'}}>
                  <p style={{margin:0, fontWeight:'700', fontSize:'14px', color:'#101828'}}>Monthly Enquiry Trend</p>
                  <p style={{margin:0, fontSize:'12px', color:'#667085'}}>Unique clients and enquiries per month for {fy}</p>
                </div>
                {monthly.length === 0 ? (
                  <div style={{padding:'60px', textAlign:'center', color:'#667085'}}>No data for {fy}</div>
                ) : (
                  <>
                    <div style={{padding:'20px', display:'flex', alignItems:'flex-end', gap:'8px', height:'180px', borderBottom:'1px solid #F2F4F7'}}>
                      {monthly.map((m, i) => (
                        <div key={i} style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'4px', height:'100%', justifyContent:'flex-end'}}>
                          <span style={{fontSize:'10px', color:'#1570EF', fontWeight:'700'}}>{m.total_enquiries}</span>
                          <div style={{width:'100%', background:'#1570EF', borderRadius:'4px 4px 0 0', height: Math.max(4, (m.total_enquiries/maxEnq)*120)+'px'}}></div>
                          <span style={{fontSize:'9px', color:'#667085', textAlign:'center', whiteSpace:'nowrap'}}>{fmtMonth(m.month)}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{overflowX:'auto'}}>
                      <table style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                        <thead><tr style={{background:'#F9FAFB'}}>
                          {['Month','Unique Clients','Enquiries'].map(h => <th key={h} style={{padding:'11px 16px', textAlign:'left', fontWeight:'600', color:'#667085', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:'1px solid #EAECF0'}}>{h}</th>)}
                        </tr></thead>
                        <tbody>{monthly.map((m, i) => (
                          <tr key={i} style={{borderBottom:'1px solid #F2F4F7'}} onMouseOver={e=>e.currentTarget.style.background='#F9FAFB'} onMouseOut={e=>e.currentTarget.style.background=''}>
                            <td style={{padding:'12px 16px', fontWeight:'600', color:'#101828'}}>{fmtMonth(m.month)}</td>
                            <td style={{padding:'12px 16px'}}><span style={{background:'#EFF4FF', color:'#1570EF', borderRadius:'6px', padding:'3px 10px', fontWeight:'600', fontSize:'12px'}}>{m.unique_clients}</span></td>
                            <td style={{padding:'12px 16px'}}><div style={{display:'flex', alignItems:'center', gap:'10px'}}><span style={{fontWeight:'700', color:'#101828', minWidth:'30px'}}>{m.total_enquiries}</span><div style={{flex:1, height:'6px', background:'#F2F4F7', borderRadius:'3px', maxWidth:'120px'}}><div style={{height:'6px', background:'#1570EF', borderRadius:'3px', width: Math.min(100,(m.total_enquiries/maxEnq)*100)+'%'}}></div></div></div></td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Pipeline ── */}
            {activeTab === 'pipeline' && (
              <div style={{background:'white', border:'1px solid #EAECF0', borderRadius:'14px', overflow:'hidden', boxShadow:'0 1px 3px rgba(16,24,40,0.05)'}}>
                <div style={{padding:'16px 20px', borderBottom:'1px solid #F2F4F7'}}>
                  <p style={{margin:0, fontWeight:'700', fontSize:'14px', color:'#101828'}}>Pipeline Funnel</p>
                  <p style={{margin:0, fontSize:'12px', color:'#667085'}}>Deal distribution across all stages for {fy}</p>
                </div>
                <div style={{padding:'20px'}}>
                  {pipeline.map((p, i) => {
                    const sc = STAGE_COLORS[p.stage] || {bg:'#F2F4F7', color:'#344054'};
                    const pct = totalPipeline > 0 ? Math.round(p.count/totalPipeline*100) : 0;
                    return (
                      <div key={i} style={{marginBottom:'16px'}}>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'6px'}}>
                          <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                            <span style={{background:sc.bg, color:sc.color, borderRadius:'6px', padding:'3px 10px', fontWeight:'600', fontSize:'12px', minWidth:'120px', textAlign:'center'}}>{STAGE_LABELS[p.stage]||p.stage}</span>
                            <span style={{fontWeight:'700', fontSize:'16px', color:'#101828'}}>{p.count}</span>
                            <span style={{fontSize:'12px', color:'#667085'}}>deals</span>
                          </div>
                          <div style={{textAlign:'right'}}>
                            <span style={{fontSize:'13px', fontWeight:'600', color:'#039855'}}>{p.value > 0 ? fmt(p.value) : '—'}</span>
                            <span style={{fontSize:'11px', color:'#667085', marginLeft:'8px'}}>{pct}%</span>
                          </div>
                        </div>
                        <div style={{height:'8px', background:'#F2F4F7', borderRadius:'4px', overflow:'hidden'}}>
                          <div style={{height:'8px', background:sc.color, borderRadius:'4px', width:pct+'%', opacity:0.8}}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── SC Performance ── */}
            {activeTab === 'sc' && (
              <div style={{background:'white', border:'1px solid #EAECF0', borderRadius:'14px', overflow:'hidden', boxShadow:'0 1px 3px rgba(16,24,40,0.05)'}}>
                <div style={{padding:'16px 20px', borderBottom:'1px solid #F2F4F7'}}>
                  <p style={{margin:0, fontWeight:'700', fontSize:'14px', color:'#101828'}}>Sales Coordinator Performance</p>
                  <p style={{margin:0, fontSize:'12px', color:'#667085'}}>Enquiries, conversions and revenue by SC for {fy}</p>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                    <thead><tr style={{background:'#F9FAFB'}}>
                      {['SC Name','Clients','Enquiries','Won','Revenue'].map(h => <th key={h} style={{padding:'11px 16px', textAlign:'left', fontWeight:'600', color:'#667085', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:'1px solid #EAECF0'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{scPerf.map((sc, i) => (
                      <tr key={i} style={{borderBottom:'1px solid #F2F4F7'}} onMouseOver={e=>e.currentTarget.style.background='#F9FAFB'} onMouseOut={e=>e.currentTarget.style.background=''}>
                        <td style={{padding:'12px 16px'}}><div style={{display:'flex', alignItems:'center', gap:'10px'}}><div style={{width:'32px', height:'32px', background:'#1570EF', borderRadius:'8px', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'700', fontSize:'13px', flexShrink:0}}>{(sc.name||'?')[0].toUpperCase()}</div><span style={{fontWeight:'600', color:'#101828'}}>{sc.name}</span></div></td>
                        <td style={{padding:'12px 16px'}}><span style={{background:'#EFF4FF', color:'#1570EF', borderRadius:'6px', padding:'2px 8px', fontWeight:'600', fontSize:'12px'}}>{sc.clients}</span></td>
                        <td style={{padding:'12px 16px', fontWeight:'600', color:'#101828'}}>{sc.enquiries}</td>
                        <td style={{padding:'12px 16px'}}><span style={{background:'#ECFDF3', color:'#039855', borderRadius:'6px', padding:'2px 8px', fontWeight:'600', fontSize:'12px'}}>{sc.won}</span></td>
                        <td style={{padding:'12px 16px', fontWeight:'700', color:'#039855'}}>{sc.revenue > 0 ? fmt(sc.revenue) : '—'}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Top Grades ── */}
            {activeTab === 'grades' && (
              <div style={{background:'white', border:'1px solid #EAECF0', borderRadius:'14px', overflow:'hidden', boxShadow:'0 1px 3px rgba(16,24,40,0.05)'}}>
                <div style={{padding:'16px 20px', borderBottom:'1px solid #F2F4F7'}}>
                  <p style={{margin:0, fontWeight:'700', fontSize:'14px', color:'#101828'}}>Top Grades by Demand</p>
                  <p style={{margin:0, fontSize:'12px', color:'#667085'}}>Most enquired grades for {fy}</p>
                </div>
                <div style={{padding:'20px'}}>
                  {topGrades.map((g, i) => (
                    <div key={i} style={{marginBottom:'14px', display:'flex', alignItems:'center', gap:'14px'}}>
                      <span style={{width:'24px', height:'24px', background:'#F2F4F7', borderRadius:'6px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:'700', color:'#344054', flexShrink:0}}>{i+1}</span>
                      <span style={{background:'#EFF4FF', color:'#1570EF', borderRadius:'6px', padding:'3px 10px', fontWeight:'700', fontSize:'13px', minWidth:'80px', textAlign:'center'}}>{g.grade}</span>
                      <div style={{flex:1, height:'8px', background:'#F2F4F7', borderRadius:'4px', overflow:'hidden'}}>
                        <div style={{height:'8px', background:'linear-gradient(90deg, #1570EF, #6941C6)', borderRadius:'4px', width: Math.round(g.count/maxGrade*100)+'%'}}></div>
                      </div>
                      <span style={{fontWeight:'700', color:'#101828', minWidth:'40px', textAlign:'right'}}>{g.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── SP Activity Tab ── */}
            {activeTab === 'spactivity' && (
              <div>
                {spLoading ? (
                  <div style={{textAlign:'center', padding:'60px', color:'#667085'}}>Loading activity data...</div>
                ) : (
                  <>
                    {/* Log Activity Button */}
                    <div style={{display:'flex', justifyContent:'flex-end', marginBottom:'16px'}}>
                      <button onClick={() => setShowLogForm(!showLogForm)} style={{padding:'9px 18px', background:'#1570EF', color:'white', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor:'pointer'}}>
                        {showLogForm ? '✕ Cancel' : '+ Log Activity'}
                      </button>
                    </div>

                    {/* Log Form */}
                    {showLogForm && (
                      <div style={{background:'white', border:'1px solid #EAECF0', borderRadius:'14px', padding:'20px', marginBottom:'20px'}}>
                        <p style={{margin:'0 0 16px', fontWeight:'700', fontSize:'14px', color:'#101828'}}>Log new activity</p>
                        {formMsg && <div style={{padding:'8px 12px', background: formMsg.startsWith('✅') ? '#ECFDF3' : '#FEF3F2', color: formMsg.startsWith('✅') ? '#039855' : '#D92D20', borderRadius:'8px', fontSize:'13px', marginBottom:'12px'}}>{formMsg}</div>}

                        {/* Salesperson picker */}
                        <p style={{margin:'0 0 8px', fontSize:'12px', fontWeight:'600', color:'#667085', textTransform:'uppercase', letterSpacing:'0.5px'}}>Salesperson</p>
                        <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'16px'}}>
                          {SALESPEOPLE.map((sp, i) => (
                            <button key={sp.id} onClick={() => handleSpSelect(sp)} style={{padding:'6px 14px', borderRadius:'8px', border:'2px solid', borderColor: formData.salesperson_id===sp.id ? SP_COLORS[i%8] : '#EAECF0', background: formData.salesperson_id===sp.id ? SP_COLORS[i%8] : 'white', color: formData.salesperson_id===sp.id ? 'white' : '#344054', fontSize:'13px', fontWeight:'500', cursor:'pointer'}}>
                              {sp.name}
                            </button>
                          ))}
                        </div>

                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'12px'}}>
                          <div>
                            <p style={{margin:'0 0 4px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Client name *</p>
                            <input value={formData.client_name} onChange={e => setFormData(f => ({...f, client_name:e.target.value}))} placeholder="e.g. Wilo Pumps GmbH" style={{width:'100%', padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}} />
                          </div>
                          <div>
                            <p style={{margin:'0 0 4px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Contact person</p>
                            <input value={formData.contact_person} onChange={e => setFormData(f => ({...f, contact_person:e.target.value}))} placeholder="e.g. Mr. Hans Mueller" style={{width:'100%', padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}} />
                          </div>
                          <div>
                            <p style={{margin:'0 0 4px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Date *</p>
                            <input type="date" value={formData.activity_date} onChange={e => setFormData(f => ({...f, activity_date:e.target.value}))} style={{width:'100%', padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}} />
                          </div>
                          <div>
                            <p style={{margin:'0 0 4px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Activity type *</p>
                            <select value={formData.activity_type} onChange={e => setFormData(f => ({...f, activity_type:e.target.value}))} style={{width:'100%', padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}}>
                              <option value="">Select...</option>
                              {['Phone call','WhatsApp','Field visit','Client visit','Video call','Trade fair','Email','Demo'].map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div>
                            <p style={{margin:'0 0 4px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Outcome *</p>
                            <select value={formData.outcome} onChange={e => { setFormData(f => ({...f, outcome:e.target.value, is_converted: e.target.value==='Order confirmed'})); }} style={{width:'100%', padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}}>
                              <option value="">Select...</option>
                              {['Interested','Quote sent','Order confirmed','Follow-up needed','Not interested'].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div>
                            <p style={{margin:'0 0 4px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Product discussed</p>
                            <input value={formData.product_discussed} onChange={e => setFormData(f => ({...f, product_discussed:e.target.value}))} placeholder="e.g. 316L 25mm bright bar" style={{width:'100%', padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}} />
                          </div>
                          <div>
                            <p style={{margin:'0 0 4px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Follow-up action</p>
                            <select value={formData.action_type} onChange={e => setFormData(f => ({...f, action_type:e.target.value}))} style={{width:'100%', padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}}>
                              <option value="">Select...</option>
                              {['Send quotation / PI','Send samples','Schedule next call','Schedule factory visit','Send MTC / certificates','Share grade comparison chart','Confirm dispatch date','No action needed','Other'].map(a => <option key={a} value={a}>{a}</option>)}
                            </select>
                          </div>
                          <div>
                            <p style={{margin:'0 0 4px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Action due date</p>
                            <input type="date" value={formData.action_due_date} onChange={e => setFormData(f => ({...f, action_due_date:e.target.value}))} style={{width:'100%', padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}} />
                          </div>
                          {formData.is_converted && (
                            <div>
                              <p style={{margin:'0 0 4px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Sale value (₹)</p>
                              <input type="number" value={formData.sale_value} onChange={e => setFormData(f => ({...f, sale_value:e.target.value}))} placeholder="e.g. 85000" style={{width:'100%', padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}} />
                            </div>
                          )}
                        </div>
                        <div>
                          <p style={{margin:'0 0 4px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Notes</p>
                          <textarea value={formData.notes} onChange={e => setFormData(f => ({...f, notes:e.target.value}))} placeholder="What was discussed..." rows={2} style={{width:'100%', padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', resize:'vertical', boxSizing:'border-box'}} />
                        </div>
                        <div style={{display:'flex', gap:'8px', marginTop:'14px', alignItems:'center'}}>
                          <button onClick={submitActivity} style={{padding:'9px 20px', background:'#1570EF', color:'white', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor:'pointer'}}>Save activity</button>
                          <label style={{display:'flex', alignItems:'center', gap:'6px', fontSize:'13px', color:'#344054', cursor:'pointer'}}>
                            <input type="checkbox" checked={formData.is_converted} onChange={e => setFormData(f => ({...f, is_converted:e.target.checked}))} /> Converted to client
                          </label>
                          <label style={{display:'flex', alignItems:'center', gap:'6px', fontSize:'13px', color:'#344054', cursor:'pointer'}}>
                            <input type="checkbox" checked={formData.is_urgent} onChange={e => setFormData(f => ({...f, is_urgent:e.target.checked}))} /> Urgent
                          </label>
                          <input value={formData.logged_by} onChange={e => setFormData(f => ({...f, logged_by:e.target.value}))} placeholder="Your name (coordinator)" style={{padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', marginLeft:'auto'}} />
                        </div>
                      </div>
                    )}

                    {/* SP Summary Table */}
                    <div style={{background:'white', border:'1px solid #EAECF0', borderRadius:'14px', overflow:'hidden', marginBottom:'20px'}}>
                      <div style={{padding:'16px 20px', borderBottom:'1px solid #F2F4F7', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <div>
                          <p style={{margin:0, fontWeight:'700', fontSize:'14px', color:'#101828'}}>Salesperson activity — {fmtMonth(spMonth)}</p>
                          <p style={{margin:0, fontSize:'12px', color:'#667085'}}>Meetings · Engagements · Conversions · Actions</p>
                        </div>
                      </div>
                      <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                          <thead><tr style={{background:'#F9FAFB'}}>
                            {['Salesperson','Region','Meetings','Engagements','Conversions','Conv %','Avg Sale','Revenue','Pending Actions'].map(h => (
                              <th key={h} style={{padding:'10px 14px', textAlign:'left', fontWeight:'600', color:'#667085', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.4px', borderBottom:'1px solid #EAECF0', whiteSpace:'nowrap'}}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {spData && spData.length > 0 ? spData.map((sp, i) => {
                              const col = SP_COLORS[i % 8];
                              const convPct = sp.conversion_rate || 0;
                              const pctColor = convPct >= 50 ? '#039855' : convPct >= 25 ? '#B54708' : '#D92D20';
                              const pctBg = convPct >= 50 ? '#ECFDF3' : convPct >= 25 ? '#FFFAEB' : '#FEF3F2';
                              return (
                                <tr key={sp.salesperson_id} style={{borderBottom:'1px solid #F2F4F7'}} onMouseOver={e=>e.currentTarget.style.background='#F9FAFB'} onMouseOut={e=>e.currentTarget.style.background=''}>
                                  <td style={{padding:'12px 14px'}}>
                                    <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                      <div style={{width:'30px', height:'30px', borderRadius:'8px', background:col, color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'700', fontSize:'12px', flexShrink:0}}>
                                        {sp.salesperson_name.slice(0,2).toUpperCase()}
                                      </div>
                                      <span style={{fontWeight:'600', color:'#101828'}}>{sp.salesperson_name}</span>
                                    </div>
                                  </td>
                                  <td style={{padding:'12px 14px', color:'#667085', fontSize:'12px'}}>{sp.region}</td>
                                  <td style={{padding:'12px 14px', fontWeight:'700', color:'#101828'}}>{sp.meetings}</td>
                                  <td style={{padding:'12px 14px', fontWeight:'700', color:'#101828'}}>{sp.engagements}</td>
                                  <td style={{padding:'12px 14px', fontWeight:'700', color:'#039855'}}>{sp.conversions}</td>
                                  <td style={{padding:'12px 14px'}}><span style={{background:pctBg, color:pctColor, borderRadius:'6px', padding:'2px 8px', fontWeight:'600', fontSize:'12px'}}>{convPct}%</span></td>
                                  <td style={{padding:'12px 14px', color:'#344054'}}>{sp.avg_sale > 0 ? fmt(sp.avg_sale) : '—'}</td>
                                  <td style={{padding:'12px 14px', fontWeight:'700', color:'#039855'}}>{sp.revenue > 0 ? fmt(sp.revenue) : '—'}</td>
                                  <td style={{padding:'12px 14px'}}>{sp.pending_actions > 0 ? <span style={{background:'#FFFAEB', color:'#B54708', borderRadius:'6px', padding:'2px 8px', fontWeight:'600', fontSize:'12px'}}>{sp.pending_actions} pending</span> : <span style={{color:'#039855', fontSize:'12px'}}>✓ All done</span>}</td>
                                </tr>
                              );
                            }) : (
                              <tr><td colSpan={9} style={{padding:'40px', textAlign:'center', color:'#667085'}}>No activities logged for {fmtMonth(spMonth)}. Click "+ Log Activity" to add one.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Recent Activities Log */}
                    <div style={{background:'white', border:'1px solid #EAECF0', borderRadius:'14px', overflow:'hidden', marginBottom:'20px'}}>
                      <div style={{padding:'16px 20px', borderBottom:'1px solid #F2F4F7'}}>
                        <p style={{margin:0, fontWeight:'700', fontSize:'14px', color:'#101828'}}>Activity log — {fmtMonth(spMonth)}</p>
                        <p style={{margin:0, fontSize:'12px', color:'#667085'}}>All logged calls, visits and meetings</p>
                      </div>
                      <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                          <thead><tr style={{background:'#F9FAFB'}}>
                            {['Date','Salesperson','Client','Type','Outcome','Follow-up action','Converted'].map(h => (
                              <th key={h} style={{padding:'10px 14px', textAlign:'left', fontWeight:'600', color:'#667085', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.4px', borderBottom:'1px solid #EAECF0', whiteSpace:'nowrap'}}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {spActivities.length > 0 ? spActivities.map((a, i) => {
                              const outcomeColor = a.outcome==='Order confirmed' ? {bg:'#ECFDF3',c:'#039855'} : a.outcome==='Not interested' ? {bg:'#FEF3F2',c:'#D92D20'} : a.outcome==='Follow-up needed' ? {bg:'#FFFAEB',c:'#B54708'} : {bg:'#EFF4FF',c:'#1570EF'};
                              return (
                                <tr key={a.id} style={{borderBottom:'1px solid #F2F4F7'}} onMouseOver={e=>e.currentTarget.style.background='#F9FAFB'} onMouseOut={e=>e.currentTarget.style.background=''}>
                                  <td style={{padding:'10px 14px', color:'#667085', fontSize:'12px', whiteSpace:'nowrap'}}>{a.activity_date}</td>
                                  <td style={{padding:'10px 14px', fontWeight:'600', color:'#101828', whiteSpace:'nowrap'}}>{a.salesperson_name}</td>
                                  <td style={{padding:'10px 14px', color:'#101828'}}>{a.client_name}</td>
                                  <td style={{padding:'10px 14px'}}><span style={{background:'#EFF4FF', color:'#1570EF', borderRadius:'6px', padding:'2px 8px', fontSize:'11px', fontWeight:'600'}}>{a.activity_type}</span></td>
                                  <td style={{padding:'10px 14px'}}><span style={{background:outcomeColor.bg, color:outcomeColor.c, borderRadius:'6px', padding:'2px 8px', fontSize:'11px', fontWeight:'600'}}>{a.outcome}</span></td>
                                  <td style={{padding:'10px 14px', color:'#667085', fontSize:'12px'}}>{a.action_type || '—'}</td>
                                  <td style={{padding:'10px 14px'}}>{a.is_converted ? <span style={{background:'#ECFDF3', color:'#039855', borderRadius:'6px', padding:'2px 8px', fontSize:'11px', fontWeight:'600'}}>Yes ✓</span> : <span style={{color:'#667085', fontSize:'12px'}}>No</span>}</td>
                                </tr>
                              );
                            }) : (
                              <tr><td colSpan={7} style={{padding:'40px', textAlign:'center', color:'#667085'}}>No activities for this month yet.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Meeting recordings (from tracker field app) */}
                    <div style={{background:'white', border:'1px solid #EAECF0', borderRadius:'14px', overflow:'hidden', marginBottom:'20px'}}>
                      {/* the shared player is a plain Audio object built in getAudio() —
                          no JSX <audio ref>, so there is no null-ref click to swallow */}
                      <div style={{padding:'16px 20px', borderBottom:'1px solid #F2F4F7', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <div>
                          <p style={{margin:0, fontWeight:'700', fontSize:'14px', color:'#101828'}}>Meeting recordings</p>
                          <p style={{margin:0, fontSize:'12px', color:'#667085'}}>Uploaded from the field app, auto-transcribed</p>
                        </div>
                        <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                          <input type="date" aria-label="From date" value={recFrom} onChange={e => setRecFrom(e.target.value)} style={{padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}} />
                          <span style={{fontSize:'12px', color:'#667085'}}>to</span>
                          <input type="date" aria-label="To date" value={recTo} onChange={e => setRecTo(e.target.value)} style={{padding:'8px 10px', border:'1px solid #D0D5DD', borderRadius:'8px', fontSize:'13px', outline:'none', boxSizing:'border-box'}} />
                        </div>
                      </div>
                      {recError ? (
                        <div style={{padding:'24px 20px', color:'#98A2B3', fontSize:'13px'}}>Recordings unavailable</div>
                      ) : (
                      <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                          <thead><tr style={{background:'#F9FAFB'}}>
                            {['Date','Salesperson','Client company','Met with','Recording','Outcome','Follow-up'].map(h => (
                              <th key={h} style={{padding:'10px 14px', textAlign:'left', fontWeight:'600', color:'#667085', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.4px', borderBottom:'1px solid #EAECF0', whiteSpace:'nowrap'}}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {spRecordings.length > 0 ? spRecordings.map((r) => {
                              const outcomeColor = r.outcome==='Order confirmed' ? {bg:'#ECFDF3',c:'#039855'} : r.outcome==='Not interested' ? {bg:'#FEF3F2',c:'#D92D20'} : r.outcome==='Follow-up needed' ? {bg:'#FFFAEB',c:'#B54708'} : {bg:'#EFF4FF',c:'#1570EF'};
                              const isOpen = expandedRec === r.id;
                              return (
                                <React.Fragment key={r.id}>
                                  <tr style={{borderBottom: isOpen ? 'none' : '1px solid #F2F4F7', cursor:'pointer'}} onClick={() => setExpandedRec(isOpen ? null : r.id)} onMouseOver={e=>e.currentTarget.style.background='#F9FAFB'} onMouseOut={e=>e.currentTarget.style.background=''}>
                                    <td style={{padding:'10px 14px', color:'#667085', fontSize:'12px', whiteSpace:'nowrap'}}>{r.visit_date}</td>
                                    <td style={{padding:'10px 14px', fontWeight:'600', color:'#101828', whiteSpace:'nowrap'}}>{r.salesperson}</td>
                                    <td style={{padding:'10px 14px', color:'#101828'}}>{r.client_company}</td>
                                    <td style={{padding:'10px 14px', color:'#344054'}}>{r.client_contact || '—'}</td>
                                    <td style={{padding:'10px 14px', whiteSpace:'nowrap'}}>
                                      {canPlayRecording(r) ? (() => {
                                        const isCur = playingId === r.id;
                                        const isPlaying = isCur && !playPaused;
                                        const failed = playErrId === r.id;
                                        return (
                                          <span style={{display:'inline-flex', alignItems:'center', gap:'8px'}}>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); togglePlay(r); }}
                                              title={isPlaying ? 'Pause' : 'Play recording'}
                                              aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
                                              style={{border:'1px solid', borderColor: isCur ? '#1570EF' : '#EAECF0', background: isCur ? '#EFF4FF' : '#F9FAFB', color: isCur ? '#1570EF' : '#344054', borderRadius:'6px', padding:'2px 9px', fontSize:'12px', cursor:'pointer', fontWeight:'600'}}
                                            >{isPlaying ? '❚❚' : '▶'}</button>
                                            <span style={{color: isCur ? '#1570EF' : '#667085', fontSize:'12px', fontFamily:'DM Mono, monospace', fontWeight: isCur ? '600' : '400'}}>
                                              {isCur
                                                ? `${fmtDur(Math.floor(playPos))} / ${fmtDur(Math.floor(playDur || r.duration_sec || 0))}`
                                                : fmtDur(r.duration_sec)}
                                            </span>
                                            {failed && <span style={{color:'#D92D20', fontSize:'11px', fontWeight:'600'}}>couldn't load audio</span>}
                                          </span>
                                        );
                                      })() : (() => {
                                        // Not playable. If we don't recognise the status, say so loudly —
                                        // a silently-hidden play button is exactly how this broke before.
                                        const st = recStatus(r);
                                        if (!REC_PENDING.has(st) && st !== 'failed') {
                                          console.warn(`[MecaPage] recording ${r.id} skipped — unrecognised status "${r.status}", duration_sec=${r.duration_sec}. Add it to REC_READY if it means "ready".`);
                                        }
                                        return (
                                          <span style={{background:'#F2F4F7', color:'#98A2B3', borderRadius:'6px', padding:'2px 8px', fontSize:'11px', fontWeight:'600'}}>
                                            {st === 'failed' ? 'failed' : 'processing'}
                                          </span>
                                        );
                                      })()}
                                    </td>
                                    <td style={{padding:'10px 14px'}}>{r.outcome ? <span style={{background:outcomeColor.bg, color:outcomeColor.c, borderRadius:'6px', padding:'2px 8px', fontSize:'11px', fontWeight:'600'}}>{r.outcome}</span> : <span style={{color:'#98A2B3', fontSize:'12px'}}>—</span>}</td>
                                    <td style={{padding:'10px 14px', color:'#667085', fontSize:'12px'}}>{r.follow_up || '—'}</td>
                                  </tr>
                                  {isOpen && (
                                    <tr style={{borderBottom:'1px solid #F2F4F7', background:'#FCFCFD'}}>
                                      <td colSpan={7} style={{padding:'14px 18px'}}>
                                        <p style={{margin:'0 0 8px', fontSize:'12px', fontWeight:'600', color:'#667085'}}>Transcript · {r.salesperson}{r.client_contact ? ` met ${r.client_contact}` : ''}</p>
                                        {r.status === 'transcribed' ? (
                                          <>
                                            <div style={{whiteSpace:'pre-wrap', fontSize:'13px', color:'#344054', lineHeight:'1.5', background:'#F9FAFB', border:'1px solid #EAECF0', borderRadius:'8px', padding:'12px', maxHeight:'260px', overflowY:'auto'}}>{r.transcript || '(empty transcript)'}</div>
                                            <button onClick={(e) => { e.stopPropagation(); if (navigator.clipboard) navigator.clipboard.writeText(r.transcript || ''); }} style={{marginTop:'10px', border:'1px solid #D0D5DD', background:'white', color:'#344054', borderRadius:'8px', padding:'6px 12px', fontSize:'12px', fontWeight:'600', cursor:'pointer'}}>Copy text</button>
                                          </>
                                        ) : (
                                          <p style={{margin:0, fontSize:'13px', color:'#98A2B3'}}>{r.status === 'failed' ? 'Transcription failed.' : 'Transcription in progress.'}</p>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            }) : (
                              <tr><td colSpan={7} style={{padding:'40px', textAlign:'center', color:'#667085'}}>No recordings between {recFrom} and {recTo}.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      )}
                    </div>

                    {/* Pending Actions */}
                    {pendingActions.length > 0 && (
                      <div style={{background:'white', border:'1px solid #EAECF0', borderRadius:'14px', overflow:'hidden'}}>
                        <div style={{padding:'16px 20px', borderBottom:'1px solid #F2F4F7'}}>
                          <p style={{margin:0, fontWeight:'700', fontSize:'14px', color:'#101828'}}>⚠️ Pending follow-up actions</p>
                          <p style={{margin:0, fontSize:'12px', color:'#667085'}}>{pendingActions.length} action{pendingActions.length>1?'s':''} not yet completed</p>
                        </div>
                        <div style={{overflowX:'auto'}}>
                          <table style={{width:'100%', borderCollapse:'collapse', fontSize:'13px'}}>
                            <thead><tr style={{background:'#F9FAFB'}}>
                              {['Salesperson','Client','Action','Due date','Status'].map(h => (
                                <th key={h} style={{padding:'10px 14px', textAlign:'left', fontWeight:'600', color:'#667085', fontSize:'11px', textTransform:'uppercase', letterSpacing:'0.4px', borderBottom:'1px solid #EAECF0'}}>{h}</th>
                              ))}
                            </tr></thead>
                            <tbody>
                              {pendingActions.map((a, i) => (
                                <tr key={a.id} style={{borderBottom:'1px solid #F2F4F7'}} onMouseOver={e=>e.currentTarget.style.background='#F9FAFB'} onMouseOut={e=>e.currentTarget.style.background=''}>
                                  <td style={{padding:'10px 14px', fontWeight:'600', color:'#101828'}}>{a.salesperson_name}</td>
                                  <td style={{padding:'10px 14px', color:'#101828'}}>{a.client_name}</td>
                                  <td style={{padding:'10px 14px', color:'#344054'}}>{a.action_type}</td>
                                  <td style={{padding:'10px 14px', color: a.overdue ? '#D92D20' : '#667085', fontWeight: a.overdue ? '600' : '400'}}>{a.action_due_date || '—'}{a.overdue ? ' ⚠️' : ''}</td>
                                  <td style={{padding:'10px 14px'}}>{a.is_urgent ? <span style={{background:'#FEF3F2', color:'#D92D20', borderRadius:'6px', padding:'2px 8px', fontSize:'11px', fontWeight:'600'}}>Urgent</span> : <span style={{background:'#FFFAEB', color:'#B54708', borderRadius:'6px', padding:'2px 8px', fontSize:'11px', fontWeight:'600'}}>Pending</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
